import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFileToBase64 from '../hooks/useFileToBase64';

const LogoUploader = ({ onLogosChange }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { base64Images, loading, error, convertToBase64, removeImage } = useFileToBase64();

  React.useEffect(() => {
    onLogosChange(base64Images);
  }, [base64Images, onLogosChange]);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      convertToBase64(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      convertToBase64(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
      <h3 className="text-lg font-bold text-[#111418] dark:text-gray-100 mb-4">
        {t('form.uploadLogo')}
      </h3>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center cursor-pointer hover:border-primary transition-colors"
      >
        <span className="material-symbols-outlined text-5xl text-gray-400">cloud_upload</span>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Drag & drop files here</p>
        <p className="text-xs text-gray-500">or</p>
        <button className="mt-2 text-sm font-medium text-primary hover:underline">
          Browse files
        </button>
        <p className="text-xs text-gray-400 mt-2">PNG, JPG, SVG (Max 5MB)</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {loading && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Loading images...
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}

      {base64Images.length > 0 && (
        <div className="mt-4 space-y-3">
          {base64Images.map((image, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-2 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div
                className="bg-center bg-no-repeat bg-contain aspect-square rounded w-10 h-10"
                style={{ backgroundImage: `url(${image.data})` }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#111418] dark:text-gray-100 truncate">
                  {image.name}
                </p>
                <p className="text-xs text-gray-500">{formatFileSize(image.size)}</p>
              </div>
              <button
                onClick={() => removeImage(index)}
                className="text-gray-400 hover:text-red-500"
              >
                <span className="material-symbols-outlined text-xl">cancel</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LogoUploader;
